
import type { UseQueryResult } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useMemo } from "react";

import type { IndeedCampaignIntegrationDto } from "./types";
import { useIndeedCampaignIntegrations } from "./useIndeedCampaignIntegrations";
import { registerCampaignsIntegrationDataHook } from "../../hooks/integrationDataHooks";
import { registerCampaignsItemsDataHook } from "../../hooks/itemsDataHooks";
import { registerCampaignEnrichment } from "../../hooks/useCampaignIntegrationsInfinite";

import type { ModuleDataValue } from "@/features/integrations/shared/renderer/hooks/types";
import { formatWhole, formatCurrency } from "@/utils/valueFormat";

/**
 * A prorated first month shows the full monthly budget sent to Indeed, with the prorated allocation
 * Indeed derives from it in parentheses.
 */
function formatCurrentMonthBudget(
  item: Pick<
    IndeedCampaignIntegrationDto,
    "currentMonthBudget" | "currentMonthMonthlyBudget"
  >
): string | null {
  if (item.currentMonthMonthlyBudget != null) {
    const monthly = formatCurrency(item.currentMonthMonthlyBudget);
    // Drop the clause entirely when there's no prorated figure — the guard above is on the monthly
    // budget, so this used to render "(prorated: N/A)".
    return item.currentMonthBudget != null
      ? `${monthly} (prorated: ${formatCurrency(item.currentMonthBudget)})`
      : monthly;
  }
  return item.currentMonthBudget != null
    ? formatCurrency(item.currentMonthBudget)
    : null;
}

registerCampaignsIntegrationDataHook("indeed", function useIndeedCampaignsIntegrationData(params) {
  const { clientId } = params;
  const queryResult = useIndeedCampaignIntegrations({ clientId });

  const transformedData = useMemo(() => {
    const summary = queryResult.data?.summary;
    if (!summary) return undefined;

    const quota = summary.apiQuota;
    return {
      totalCampaigns: summary.totalCampaigns,
      activeOnIndeed: summary.activeOnIndeed,
      syncedCount: summary.syncedCount,
      syncedLabel: summary.syncedCount,
      syncedSubtitle: `Of ${formatWhole(summary.totalCampaigns)} Indeed campaigns`,
      quotaLabel: quota
        ? `${formatWhole(quota.requestsUsed)} / ${formatWhole(quota.quotaLimit)}`
        : "—",
      quotaSubtitle: quota
        ? `${formatWhole(quota.requestsRemaining)} requests remaining`
        : "No quota data available",
    };
  }, [queryResult.data?.summary]);

  return {
    ...queryResult,
    data: transformedData,
  } as unknown as UseQueryResult<Record<string, ModuleDataValue>, AxiosError>;
});

registerCampaignsItemsDataHook("indeed", (clientId) =>
  clientId ? useIndeedCampaignsItemsDataImpl : undefined
);

function useIndeedCampaignsItemsDataImpl(params: {
  clientId: string | undefined;
  tenantId: string | undefined;
  page: number;
  pageSize: number;
  search?: string;
  filterValues?: Record<string, string>;
}) {
  const { clientId, page, pageSize, search, filterValues } = params;
  const statusFilter = filterValues?.statusFilter;
  const queryResult = useIndeedCampaignIntegrations({ clientId, page, pageSize, search, statusFilter });

  const transformedData = useMemo(() => {
    if (!queryResult.data) return undefined;

    const enrichedItems = (queryResult.data.items ?? []).map((item: IndeedCampaignIntegrationDto) => {
      const falconStatusBadge = item.falconStatus === "Active"
        ? { label: "common.active", variant: "Success" }
        : item.falconStatus === "Paused"
          ? { label: "common.paused", variant: "Warning" }
          : { label: "common.inactive", variant: "Default" };

      const syncStatusBadge = item.syncStatus === "Synced"
        ? { label: "integrations.indeed.campaigns.synced", variant: "Success" }
        : item.syncStatus === "NotSynced"
          ? { label: "integrations.indeed.campaigns.notSynced", variant: "Warning" }
          : { label: "integrations.indeed.campaigns.failed", variant: "Error" };

      const startDateFormatted = item.startDate ? new Date(item.startDate).toLocaleDateString() : "";
      const endDateFormatted = item.endDate ? new Date(item.endDate).toLocaleDateString() : "—";

      const budgetFormatted = item.monthlyBudget != null
        ? formatCurrency(item.monthlyBudget)
        : "";

      const currentMonthBudgetFormatted = formatCurrentMonthBudget(item);
      const remoteBudgetFormatted = item.remoteBudget != null
        ? formatCurrency(item.remoteBudget)
        : null;

      const remoteStatusUpper = item.indeedStatus?.toUpperCase();
      const indeedStatusLabel = remoteStatusUpper === "ACTIVE" ? "Active"
        : remoteStatusUpper === "PAUSED" ? "Paused"
          : "Inactive";
      const indeedStatusInline = item.syncStatus === "Failed"
        ? { label: "integrations.indeed.campaigns.issuesFound", variant: "warning" }
        : remoteStatusUpper === "ACTIVE"
          ? { label: "integrations.indeed.campaigns.indeedActive", variant: "success" }
          : remoteStatusUpper === "PAUSED"
            ? { label: "common.paused", variant: "warning" }
            : { label: "integrations.indeed.campaigns.indeedInactive", variant: "neutral" };

      const remoteStatusValue = remoteStatusUpper === "ACTIVE" ? "Active"
        : remoteStatusUpper === "PAUSED" ? "Paused"
          : "Inactive";

      return {
        ...item,
        falconStatusBadge,
        syncStatusBadge,
        startDateFormatted,
        endDateFormatted,
        budgetFormatted,
        currentMonthBudgetFormatted,
        remoteBudgetFormatted,
        indeedStatusLabel,
        indeedStatusInline,
        remoteStatusValue,
      };
    });

    return {
      data: enrichedItems,
      totalRecords: queryResult.data.totalRecords,
    };
  }, [queryResult.data]);

  return {
    ...queryResult,
    data: transformedData,
  } as unknown as UseQueryResult<{ data: unknown[]; totalRecords: number }, AxiosError>;
}

registerCampaignEnrichment("indeed", (items) =>
  (items as IndeedCampaignIntegrationDto[]).map((item) => {
    const falconStatusBadge = item.falconStatus === "Active"
      ? { label: "common.active", variant: "Success" }
      : item.falconStatus === "Paused"
        ? { label: "common.paused", variant: "Warning" }
        : { label: "common.inactive", variant: "Default" };

    const syncStatusBadge = item.syncStatus === "Synced"
      ? { label: "integrations.indeed.campaigns.synced", variant: "Success" }
      : item.syncStatus === "NotSynced"
        ? { label: "integrations.indeed.campaigns.notSynced", variant: "Warning" }
        : { label: "integrations.indeed.campaigns.failed", variant: "Error" };

    const startDateFormatted = item.startDate ? new Date(item.startDate).toLocaleDateString() : "";
    const endDateFormatted = item.endDate ? new Date(item.endDate).toLocaleDateString() : "—";

    const budgetFormatted = item.monthlyBudget != null
      ? formatCurrency(item.monthlyBudget)
      : "";

    const currentMonthBudgetFormatted = formatCurrentMonthBudget(item);
    const remoteBudgetFormatted = item.remoteBudget != null
      ? formatCurrency(item.remoteBudget)
      : null;

    const remoteStatusUpper = item.indeedStatus?.toUpperCase();
    const indeedStatusLabel = remoteStatusUpper === "ACTIVE" ? "Active"
      : remoteStatusUpper === "PAUSED" ? "Paused"
        : "Inactive";
    const indeedStatusInline = item.syncStatus === "Failed"
      ? { label: "integrations.indeed.campaigns.issuesFound", variant: "warning" }
      : remoteStatusUpper === "ACTIVE"
        ? { label: "integrations.indeed.campaigns.indeedActive", variant: "success" }
        : remoteStatusUpper === "PAUSED"
          ? { label: "common.paused", variant: "warning" }
          : { label: "integrations.indeed.campaigns.indeedInactive", variant: "neutral" };

    const remoteStatusValue = remoteStatusUpper === "ACTIVE" ? "Active"
      : remoteStatusUpper === "PAUSED" ? "Paused"
        : "Inactive";

    return {
      ...item,
      falconStatusBadge,
      syncStatusBadge,
      startDateFormatted,
      endDateFormatted,
      budgetFormatted,
      currentMonthBudgetFormatted,
      remoteBudgetFormatted,
      indeedStatusLabel,
      indeedStatusInline,
      remoteStatusValue,
    };
  }),
);

export { useIndeedCampaignIntegrations } from "./useIndeedCampaignIntegrations";
export type {
  IndeedCampaignIntegrationDto,
  IndeedCampaignIntegrationsResponse,
} from "./types";
